import React, { useContext, useState } from 'react';
import {
  ICventContact,
  ICventRole,
  useCreateCventContactMutation,
  useGetAllCventRolesQuery,
  useUpdateCventContactMutation
} from '../../generated/graphql';
import DialogBox from '../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import EditButton from '../Common/Buttons/EditButton';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import { getNullableDateString, parseDate } from '../../utils/clientUtils';
import { LocalizationProvider } from '@mui/x-date-pickers';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import InputComponent from '../Common/Fields/InputComponent';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Colors } from '../../utils/theme';

interface IProps {
  handleClose: () => void;
  cventContact: ICventContact | null;
  locationId: number | null;
  updateList: (contact: ICventContact) => void;
  editList: (contact: ICventContact) => void;
}

type IErrors = {
  createdDate: string | null;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
};

const defaultErrorsObj: IErrors = {
  createdDate: null,
  first_name: null,
  last_name: null,
  company_name: null,
  email: null
};
//
const errorTexts = {
  createdDate: 'Created date is required!',
  first_name: 'First name is required!',
  last_name: 'Last name is required',
  company_name: 'Company name is required',
  email: 'Email is required'
};

const CventForm: React.FunctionComponent<IProps> = ({
  handleClose,
  cventContact,
  locationId,
  updateList,
  editList
}: IProps) => {
  const [editEnabled, setEditEnabled] = useState(cventContact === null);
  const { dispatch } = useContext(AppContext);
  const [createdDate, setCreatedDate] = useState<Date | null>(
    cventContact ? parseDate(cventContact.date_created) : null
  );
  const allRoles = useGetAllCventRolesQuery();
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [selectedRole, setSelectedRole] = useState<ICventRole | null>(cventContact?.role || null);
  const [firstName, setFirstName] = useState<string>(cventContact?.first_name || '');
  const [lastName, setLastName] = useState<string>(cventContact?.last_name || '');
  const [companyName, setCompanyName] = useState<string | null>(cventContact?.company_name || null);
  const [email, setEmail] = useState<string | null>(cventContact?.email || null);
  const [createCventContact, createCventContactResult] = useCreateCventContactMutation();
  const [updateCventContact, updateCventContactResult] = useUpdateCventContactMutation();

  const handleDateChange = (input: Date | null) => {
    if (input) {
      if (!Number.isNaN(input.getDate())) {
        setFormErrors({ ...formErrors, createdDate: null });
      }
    } else {
      setFormErrors({ ...formErrors, createdDate: errorTexts.createdDate });
    }
    setCreatedDate(input);
  };

  const handleRoleChange = (_: unknown, newValue: React.SetStateAction<ICventRole | null>) => {
    setSelectedRole(newValue);
  };

  const handleFirstNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        first_name: errorTexts.first_name
      });
    } else {
      setFormErrors({ ...formErrors, first_name: null });
    }
    setFirstName(value);
  };

  const handleLastNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        last_name: errorTexts.last_name
      });
    } else {
      setFormErrors({ ...formErrors, last_name: null });
    }
    setLastName(value);
  };

  const handleCompanyNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        company_name: errorTexts.company_name
      });
    } else {
      setFormErrors({ ...formErrors, company_name: null });
    }
    setCompanyName(value);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        email: errorTexts.email
      });
    } else {
      setFormErrors({ ...formErrors, email: null });
    }
    setEmail(value);
  };

  const handleSave = () => {
    let hasErrors = false;
    const errs: IErrors = {
      createdDate: null,
      first_name: null,
      last_name: null,
      company_name: null,
      email: null
    };
    if (firstName.length < 2) {
      hasErrors = true;
      errs.first_name = errorTexts.first_name;
    }
    if (lastName.length < 2) {
      hasErrors = true;
      errs.last_name = errorTexts.last_name;
    }
    if (createdDate === null) {
      hasErrors = true;
      errs.createdDate = errorTexts.createdDate;
    }
    if (hasErrors) {
      setFormErrors(errs);
    } else {
      submitContact();
    }
  };

  const submitContact = () => {
    const input = {
      first_name: firstName as string,
      last_name: lastName as string,
      company_name: companyName as string,
      role_id: Number(selectedRole?.id as string),
      email: email as string,
      date_created: getNullableDateString(createdDate) as string,
      location_id: locationId as number,
      is_approved: false
    };
    if (cventContact !== null) {
      updateCventContact({
        variables: {
          id: (cventContact as ICventContact).id,
          input
        },
        update(_, { data }) {
          if (data?.result) {
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `You have successfully updated ${input.first_name} ${input.last_name}`
              }
            });
            handleClose();
            editList(data?.result as ICventContact);
          }
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    } else {
      createCventContact({
        variables: {
          input
        },
        update(_, { data }) {
          if (data?.result) {
            updateList(data.result as ICventContact);
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `Registrant ${input.first_name} ${input.last_name} created`
              }
            });
            handleClose();
          }
        }
      }).catch((e) => {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        });
      });
    }
  };

  return (
    <DialogBox
      id="new-cvent-contact-registree-dialog"
      width={589}
      height={640}
      title={cventContact ? 'Cvent Contact Details' : 'Create Registrant'}
      onClose={handleClose}
    >
      <Box sx={{ padding: '70px', position: 'relative' }}>
        {cventContact && (
          <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
            <EditButton
              id="create-registrant-edit-button"
              onClick={() => setEditEnabled(true)}
              disabled={editEnabled}
              editButtonColor={editEnabled ? Colors.gray : Colors.skyBlue}
            />
          </Box>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '62px 22px'
          }}
        >
          <InputComponent
            id="first-name"
            label="First Name"
            value={firstName}
            onChange={handleFirstNameChange}
            errorMsg={formErrors.first_name}
            disabled={
              !editEnabled || createCventContactResult.loading || updateCventContactResult.loading
            }
            additionalStyle={{ gridColumn: 'span 2' }}
          />
          <InputComponent
            id="last-name"
            label="Last Name"
            value={lastName}
            onChange={handleLastNameChange}
            errorMsg={formErrors.last_name}
            disabled={
              !editEnabled || createCventContactResult.loading || updateCventContactResult.loading
            }
            additionalStyle={{ gridColumn: 'span 2' }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <EpraDatePicker
              id="cvent-create-registrant-created-date"
              label="Created Date"
              value={createdDate}
              format={'dd/MM/yyyy'}
              errorMsg={formErrors.createdDate}
              clearable={true}
              onChange={(d) => handleDateChange(d)}
              disabled={
                !editEnabled || createCventContactResult.loading || updateCventContactResult.loading
              }
              sx={{ gridColumn: 'span 2' }}
            />
          </LocalizationProvider>
          <AutocompleteComponent
            id="cvent-registrant-role-autocomplete"
            options={(allRoles.data?.result as never[]) || []}
            getOptionLabel={(option: ICventRole) => option.name as string}
            values={selectedRole}
            onChange={handleRoleChange}
            label="Role"
            disabled={
              !editEnabled || createCventContactResult.loading || updateCventContactResult.loading
            }
            additionalStyle={{ gridColumn: 'span 2' }}
          />
          <InputComponent
            id="company-name"
            label="Company Name"
            value={companyName}
            onChange={handleCompanyNameChange}
            disabled={
              !editEnabled || createCventContactResult.loading || updateCventContactResult.loading
            }
            additionalStyle={{ gridColumn: 'span 4' }}
          />
          <InputComponent
            id="email"
            label="Email"
            value={email}
            onChange={handleEmailChange}
            disabled={
              !editEnabled || createCventContactResult.loading || updateCventContactResult.loading
            }
            additionalStyle={{ gridColumn: 'span 4' }}
            errorMsg={formErrors.email}
          />
        </Box>
        {editEnabled && (
          <Box className="centralize" sx={{ marginTop: '60px' }}>
            <CancelButton
              id="cancel-button"
              onClick={handleClose}
              text={cventContact === null || editEnabled ? 'Cancel' : 'Close'}
            />
            <SaveButton
              id="save-button"
              onClick={handleSave}
              disabled={
                !editEnabled || createCventContactResult.loading || updateCventContactResult.loading
              }
              text={cventContact ? 'Save' : 'Create'}
            />
          </Box>
        )}
      </Box>
    </DialogBox>
  );
};

export default CventForm;
